import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { components, operations } from "../../types/api";

// Tipos generados desde el swagger del backend (yarn generate:types:local).
// No se escriben a mano: el contrato lo define TSOA.
export type CommercialProposalListItem =
  components["schemas"]["CommercialProposalListItem"];
export type CommercialProposalView =
  components["schemas"]["CommercialProposalView"];
export type CommercialProposalCreated =
  components["schemas"]["CommercialProposalCreated"];
export type ProposalProjectInput =
  components["schemas"]["ProposalProjectInput"];
export type ProposalScenarioInput =
  components["schemas"]["ProposalScenarioInput"];
export type ContentTagOption = components["schemas"]["ContentTagOption"];
export type CommercialProposalFilterOptions =
  components["schemas"]["CommercialProposalFilterOptions"];
export type CrmProspectOption = components["schemas"]["CrmProspectOption"];
export type CrmStageEntry = components["schemas"]["CrmStageEntry"];
export type ProposalStatus = components["schemas"]["ProposalStatus"];
export type ProposalVersionRef = components["schemas"]["ProposalVersionRef"];
export type ProposalVersionDiff = components["schemas"]["ProposalVersionDiff"];
export type ProposalVersionChange =
  components["schemas"]["ProposalVersionChange"];
export type ProposalStatusChanged =
  components["schemas"]["ProposalStatusChanged"];
export type CreateCommercialProposalRequest =
  components["schemas"]["CreateCommercialProposalRequest"];

export type CommercialBusinessUnit =
  components["schemas"]["CommercialBusinessUnit"];
export type CommercialClientKind =
  components["schemas"]["CommercialClientKind"];
export type ClientEngagement = components["schemas"]["ClientEngagement"];

/** Los tipos de cliente, en el orden en que se ofrecen en pantalla. */
export const CLIENT_ENGAGEMENTS: ClientEngagement[] = [
  "ALWAYS_ON",
  "EVENTUAL",
  "ESTACIONAL",
  "TEST",
];
export type House = components["schemas"]["House"];
export type ProjectBudgetType = components["schemas"]["ProjectBudgetType"];
export type CommercialBriefingCrmStatus =
  components["schemas"]["CommercialBriefingCrmStatus"];

type ListResponse =
  components["schemas"]["ApiResponse_PaginatedCommercialProposalResult_"];
type ViewResponse =
  components["schemas"]["ApiResponse_CommercialProposalView_"];
type CreatedResponse =
  components["schemas"]["ApiResponse_CommercialProposalCreated_"];
type DeletedResponse =
  components["schemas"]["ApiResponse_CommercialProposalDeleted_"];
type FilterOptionsResponse =
  components["schemas"]["ApiResponse_CommercialProposalFilterOptions_"];
type TagsResponse =
  components["schemas"]["ApiResponse_ContentTagOption-Array_"];
type ProspectsResponse =
  components["schemas"]["ApiResponse_CrmProspectOption-Array_"];
type StagesResponse = components["schemas"]["ApiResponse_CrmStageEntry-Array_"];
type StatusChangedResponse =
  components["schemas"]["ApiResponse_ProposalStatusChanged_"];
type VersionDiffResponse =
  components["schemas"]["ApiResponse_ProposalVersionDiff_"];

type ListParams = operations["GetProposals"]["parameters"]["query"];

export const commercialProposalApi = createApi({
  reducerPath: "commercialProposalApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["CommercialProposals"],
  endpoints: (builder) => ({
    // GET /content-tags — catálogo de tags de los proyectos. Es una tabla en el backend,
    // no un enum, así que puede crecer sin tocar el front.
    getContentTags: builder.query<TagsResponse, void>({
      query: () => "/commercial-proposals/content-tags",
    }),

    // GET /filter-options — años y personas que aparecen en alguna propuesta. Lleva el
    // tag del listado para que una propuesta nueva con otro comercial salga en el filtro.
    getProposalFilterOptions: builder.query<FilterOptionsResponse, void>({
      query: () => "/commercial-proposals/filter-options",
      providesTags: ["CommercialProposals"],
    }),

    // GET /crm-prospects?q=&businessUnit= — buscador contra el CRM en vivo.
    // El backend limita a 30 búsquedas/minuto por usuario y cachea 60 s, así que hay que
    // llamar con debounce y desde 3 caracteres. Un 429 NO es un error a pintar en rojo:
    // significa esperar unos segundos.
    searchCrmProspects: builder.query<
      ProspectsResponse,
      { q: string; businessUnit: CommercialBusinessUnit }
    >({
      query: ({ q, businessUnit }) =>
        `/commercial-proposals/crm-prospects?q=${encodeURIComponent(q)}&businessUnit=${businessUnit}`,
    }),

    // GET /crm-stages?ids= — la etapa actual de varias oportunidades.
    // No está en nuestra tabla a propósito: la mueve el comercial arrastrando tarjetas
    // en Twenty, así que una copia local quedaría obsoleta al primer arrastre. Si el
    // CRM no responde, la columna sale vacía y el listado sigue funcionando.
    getCrmStages: builder.query<StagesResponse, string[]>({
      query: (ids) =>
        `/commercial-proposals/crm-stages?ids=${encodeURIComponent(ids.join(","))}`,
    }),

    // POST / — compone el número, guarda y crea la oportunidad.
    // Devuelve 201 incluso si el CRM falló: en ese caso `crmStatus` es ERROR y el número
    // sigue siendo válido, así que NO hay que reenviar el formulario (emitiría una V2).
    createCommercialProposal: builder.mutation<
      CreatedResponse,
      CreateCommercialProposalRequest
    >({
      query: (body) => ({
        url: "/commercial-proposals",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CommercialProposals"],
    }),

    listCommercialProposals: builder.query<ListResponse, ListParams | void>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.page) search.set("page", String(params.page));
        if (params?.pageSize) search.set("pageSize", String(params.pageSize));
        if (params?.businessUnit)
          search.set("businessUnit", params.businessUnit);
        if (params?.crmStatus) search.set("crmStatus", params.crmStatus);
        if (params?.clientAccountId)
          search.set("clientAccountId", params.clientAccountId);
        if (params?.search) search.set("search", params.search);
        if (params?.status) search.set("status", params.status);
        if (params?.year) search.set("year", String(params.year));
        if (params?.responsableComercialUserId)
          search.set(
            "responsableComercialUserId",
            params.responsableComercialUserId
          );
        if (params?.responsablePropuestaUserId)
          search.set(
            "responsablePropuestaUserId",
            params.responsablePropuestaUserId
          );
        if (params?.house) search.set("house", params.house);
        // Solo las vigentes: lo pide el tablero. Una propuesta sustituida por su V2 ya
        // no esta en curso, y tenerla en una columna la contaria dos veces.
        if (params?.onlyCurrent) search.set("onlyCurrent", "true");
        const qs = search.toString();
        return `/commercial-proposals${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["CommercialProposals"],
    }),

    getCommercialProposal: builder.query<ViewResponse, string>({
      query: (id) => `/commercial-proposals/${id}`,
      providesTags: ["CommercialProposals"],
    }),

    // GET /{id}/version-changes — que cambio respecto a la version anterior.
    // Endpoint aparte de la ficha: compara dos propuestas enteras con sus proyectos, y
    // la ficha se abre muchas mas veces de las que alguien mira los cambios.
    getProposalVersionChanges: builder.query<VersionDiffResponse, string>({
      query: (id) => `/commercial-proposals/${id}/version-changes`,
      providesTags: ["CommercialProposals"],
    }),

    // POST /{id}/retry — idempotente: si la oportunidad ya existe se adopta en vez de
    // duplicarla, y una propuesta ya SYNCED no se reenvía.
    retryCommercialProposal: builder.mutation<CreatedResponse, string>({
      query: (id) => ({
        url: `/commercial-proposals/${id}/retry`,
        method: "POST",
      }),
      invalidatesTags: ["CommercialProposals"],
    }),

    // PATCH /{id}/status — mueve la propuesta de columna, y con ella la etapa de la
    // oportunidad en Twenty. Devuelve 200 aunque el CRM no se haya movido: el estado
    // local SI se guardo, y un error haria que la tarjeta volviese sola a su sitio.
    // El motivo, cuando lo hay, viene en `message`.
    updateProposalStatus: builder.mutation<
      StatusChangedResponse,
      { id: string; status: ProposalStatus }
    >({
      query: ({ id, status }) => ({
        url: `/commercial-proposals/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["CommercialProposals"],
    }),

    // DELETE /{id} — solo ADMIN. Borra la propuesta y su oportunidad en el CRM.
    // NO borra la cuenta del registro: si era un prospect dado de alta con esta
    // propuesta, conserva su número.
    deleteCommercialProposal: builder.mutation<DeletedResponse, string>({
      query: (id) => ({
        url: `/commercial-proposals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CommercialProposals"],
    }),
  }),
});

export const {
  useGetContentTagsQuery,
  useGetProposalFilterOptionsQuery,
  useGetCrmStagesQuery,
  useUpdateProposalStatusMutation,
  useLazySearchCrmProspectsQuery,
  useCreateCommercialProposalMutation,
  useListCommercialProposalsQuery,
  useGetCommercialProposalQuery,
  useGetProposalVersionChangesQuery,
  useRetryCommercialProposalMutation,
  useDeleteCommercialProposalMutation,
} = commercialProposalApi;
